import React, { useEffect, useState } from "react";
import { ShieldAlert, RefreshCw, PowerOff, Power, AlertTriangle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { aiApi, formatApiError } from "../../lib/ledgerApi";
import { toast } from "sonner";

const ACTION_KINDS = [
  { key: "*", label: "All actions" },
  { key: "transfer", label: "Transfer" },
  { key: "invoice_approve", label: "Invoice approve" },
];
const FORCE_MODES = [
  { key: "off", label: "Off (normal scoring)" },
  { key: "hold", label: "Force HOLD (202 + ticket)" },
  { key: "block", label: "Force BLOCK" },
];

const decisionPill = (d) => {
  const map = {
    block: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
    hold: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
    allow_medium: "border-status-info/40 bg-status-info/10 text-status-info",
    allow_low: "border-status-ok/40 bg-status-ok/10 text-status-ok",
  };
  return map[d] || "border-border bg-muted";
};

export default function AdminRisk() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [metrics, setMetrics] = useState(null);
  const [degradation, setDegradation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [forceOverrides, setForceOverrides] = useState({});
  const [forceActionKind, setForceActionKind] = useState("invoice_approve");
  const [forceMode, setForceMode] = useState("off");
  const [forceBusy, setForceBusy] = useState(false);

  const load = async () => {
    try {
      const params = filter !== "all" ? { decision: filter } : {};
      const [decisions, m, d, fd] = await Promise.all([
        aiApi.riskDecisions(params),
        aiApi.aiMetrics(),
        aiApi.getDegradation(),
        aiApi.getForceDecision().catch(() => ({ overrides: {} })),
      ]);
      setItems(decisions);
      setMetrics(m);
      setDegradation(d);
      setForceOverrides(fd.overrides || {});
      const cur = (fd.overrides || {})[forceActionKind];
      setForceMode(cur || "off");
    } catch (e) {
      toast.error("Load failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  const onApplyForce = async () => {
    setForceBusy(true);
    try {
      const r = await aiApi.setForceDecision(forceMode, forceActionKind);
      setForceOverrides(r.overrides || {});
      toast.success(
        forceMode === "off" ? "Override cleared" : `Forcing ${forceMode.toUpperCase()}`,
        { description: `action_kind=${forceActionKind}` },
      );
    } catch (e) {
      toast.error("Override failed", {
        description: formatApiError(e.response?.data?.detail),
      });
    } finally {
      setForceBusy(false);
    }
  };

  const onToggleDegradation = async () => {
    setToggling(true);
    try {
      const next = !degradation?.forced_offline;
      const r = await aiApi.setDegradation(next);
      setDegradation((d) => ({ ...(d || {}), ...r }));
      toast.success(next ? "AI offline simulation ON" : "AI restored online", {
        description: next
          ? "Fraud decisions now run heuristic-only; LLM scores will be null."
          : "LLM blending re-enabled for borderline cases.",
      });
    } catch (e) {
      toast.error("Toggle failed", { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setToggling(false);
    }
  };

  useEffect(() => {
    load();
  }, [filter]); // eslint-disable-line

  return (
    <div className="max-w-6xl px-6 md:px-10 py-10 md:py-14" data-testid="admin-risk-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Super-admin · Risk</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
            <ShieldAlert className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} /> Risk decisions
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Every transfer attempt produces a deterministic verdict. The LLM only blends in for borderline cases.
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="risk-filter" className="w-[200px] rounded-sm font-mono">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All decisions</SelectItem>
              <SelectItem value="block">Blocked</SelectItem>
              <SelectItem value="hold">Held</SelectItem>
              <SelectItem value="allow_medium">Allow medium</SelectItem>
              <SelectItem value="allow_low">Allow low</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={load}
            data-testid="risk-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs border border-border rounded-sm hover:border-foreground"
          >
            <RefreshCw className="w-3 h-3" strokeWidth={1.5} /> Refresh
          </button>
        </div>
      </div>

      {/* AI degradation toggle */}
      {degradation && (
        <div data-testid="degradation-card" className={`mb-6 border rounded-sm p-4 flex items-center justify-between gap-4 ${degradation.forced_offline ? "border-amber-500/40 bg-amber-500/10" : "border-border bg-card"}`}>
          <div className="flex items-center gap-3">
            {degradation.forced_offline
              ? <PowerOff className="w-4 h-4 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
              : <Power className="w-4 h-4 text-status-ok" strokeWidth={1.5} />}
            <div>
              <div className="text-sm font-medium">
                AI {degradation.available ? "online" : "offline"}
                {degradation.forced_offline && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400">
                    runtime override
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                key {degradation.key_present ? "present" : "missing"}
                {degradation.env_force_timeout && " · env LLM_FORCE_TIMEOUT=true"}
              </div>
            </div>
          </div>
          <button
            onClick={onToggleDegradation}
            disabled={toggling}
            data-testid="degradation-toggle"
            className={`px-3 py-2 text-xs uppercase tracking-widest border rounded-sm transition-colors disabled:opacity-50 ${
              degradation.forced_offline
                ? "border-status-ok text-status-ok hover:bg-status-ok hover:text-background"
                : "border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-500 hover:text-background"
            }`}
          >
            {degradation.forced_offline ? "Restore AI" : "Simulate outage"}
          </button>
        </div>
      )}

      {/* DEV-only fraud force-decision (hold / block) */}
      <div
        data-testid="force-decision-card"
        className={`mb-6 border rounded-sm p-4 ${
          Object.keys(forceOverrides).length > 0
            ? "border-amber-500/40 bg-amber-500/10"
            : "border-border bg-card"
        }`}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
            <div>
              <div className="text-sm font-medium">
                Fraud force-decision
                <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400">
                  dev only
                </span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">
                Override the fraud engine for a specific action_kind to deterministically
                exercise hold/block paths. Resets on backend restart.
              </div>
              {Object.keys(forceOverrides).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="force-decision-active">
                  {Object.entries(forceOverrides).map(([k, v]) => (
                    <span
                      key={k}
                      className="px-1.5 py-0.5 border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 rounded-sm font-mono text-[10px]"
                    >
                      {k} → {v}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Action kind</div>
            <Select
              value={forceActionKind}
              onValueChange={(v) => {
                setForceActionKind(v);
                setForceMode(forceOverrides[v] || "off");
              }}
            >
              <SelectTrigger data-testid="force-action-select" className="w-[200px] rounded-sm font-mono">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTION_KINDS.map((a) => (
                  <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Mode</div>
            <Select value={forceMode} onValueChange={setForceMode}>
              <SelectTrigger data-testid="force-mode-select" className="w-[240px] rounded-sm font-mono">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FORCE_MODES.map((m) => (
                  <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={onApplyForce}
            disabled={forceBusy}
            data-testid="force-apply-btn"
            className="px-3 py-2 text-xs uppercase tracking-widest border border-foreground bg-foreground text-background rounded-sm hover:opacity-90 disabled:opacity-50"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Spacer end of force section, continue below */}
      {false && (
        <div></div>
      )}

      {/* AI metrics */}
      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {[
            ["LLM available", metrics.available ? "yes" : "no"],
            ["Calls", metrics.calls],
            ["Cache hits", metrics.cache_hits],
            ["Timeouts", metrics.timeouts],
            ["Avg latency", `${(metrics.avg_latency_ms || 0).toFixed(0)} ms`],
          ].map(([k, v]) => (
            <div key={k} className="p-4 border border-border bg-card rounded-sm" data-testid={`ai-metric-${k.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{k}</div>
              <div className="font-mono text-lg">{String(v)}</div>
            </div>
          ))}
        </div>
      )}

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="border border-border rounded-sm bg-card p-8 text-center text-muted-foreground text-sm">
          No decisions yet.
        </div>
      )}

      <div className="border border-border rounded-sm bg-card divide-y divide-border">
        {items.map((d, i) => (
          <div key={i} className="p-4 grid grid-cols-12 gap-3 items-center text-sm">
            <div className="col-span-3 font-mono text-[11px] text-muted-foreground truncate">
              {new Date(d.computed_at).toLocaleString()}
              {d.tx_id && (
                <div className="text-[10px] mt-0.5 truncate">tx {d.tx_id.slice(0, 16)}…</div>
              )}
            </div>
            <div className="col-span-2">
              <span className={`px-2 py-1 rounded-sm border text-[11px] font-mono ${decisionPill(d.decision)}`}>
                {d.decision}
              </span>
            </div>
            <div className="col-span-1 font-mono text-right">{d.final_score}/100</div>
            <div className="col-span-6 text-[12px] text-muted-foreground">
              <div className="truncate">{d.rationale}</div>
              {d.signals?.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {d.signals.slice(0, 6).map((s, j) => (
                    <span key={j} className="px-1 py-0.5 border border-border bg-muted rounded-sm font-mono text-[10px]">
                      {s.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
